// Componente de seleção segmentada (escolha única entre poucas opções).
// Usado para Gênero (Feminino/Masculino) e Documento (CPF/Passaporte/Documento Nacional).
import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import './segmented_field.css';

// ── Tipos ──────────────────────────────────────────────────────────────────────

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

export interface SegmentedFieldProps<T extends string> {
  label: string;
  options: SegmentedOption<T>[];
  /** Ausente: nenhuma opção marcada (o gênero que o perfil ainda não tem). */
  value?: T;
  onChange: (value: T) => void;
  /** Ao lado do rótulo (ex.: o «Por que?» do gênero). */
  labelAside?: ReactNode;
  /** Id do texto que descreve o grupo para leitor de tela. */
  describedBy?: string;
}

// ── Componente ─────────────────────────────────────────────────────────────────

const STEP: Partial<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/**
 * Padrão de radiogroup do WAI-ARIA: um só ponto de Tab (a opção marcada, ou a
 * primeira sem valor) e as setas movem o foco marcando a opção.
 */
export function SegmentedField<T extends string>({
  label,
  options,
  value,
  onChange,
  labelAside,
  describedBy,
}: Readonly<SegmentedFieldProps<T>>) {
  const labelId = useId();
  const group = useRef<HTMLDivElement>(null);
  const tabbable = options.some((opt) => opt.value === value) ? value : options[0]?.value;

  const select = (next: T) => {
    if (next !== value) onChange(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = STEP[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const nextIndex = (index + step + options.length) % options.length;
    const next = options[nextIndex];
    if (!next) return;
    select(next.value);
    const radios = group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    radios?.[nextIndex]?.focus();
  };

  return (
    <div className="seg">
      <div className="seg-head">
        <span className="seg-label" id={labelId}>
          {label}
        </span>
        {labelAside}
      </div>
      <div
        ref={group}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        className="seg-group"
      >
        {options.map((opt, index) => {
          const isActive = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              className={`seg-opt${isActive ? ' seg-opt--active' : ''}`}
              aria-checked={isActive}
              tabIndex={opt.value === tabbable ? 0 : -1}
              onClick={() => {
                select(opt.value);
              }}
              onKeyDown={(event) => {
                onKeyDown(event, index);
              }}
            >
              {opt.icon}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
